/**
 * NoVendorKeysBanner — reusable CTA card shown when a page needs a
 * vendor key the current user hasn't configured.
 *
 * Self-gates on the auth store. Pages drop it at the top of their
 * render without managing visibility state. If the required vendor key
 * is already configured (or auth hasn't loaded yet), renders null.
 *
 * Usage:
 *   <NoVendorKeysBanner vendor="meraki" />     // gate on merakiKeyConfigured
 *   <NoVendorKeysBanner vendor="aruba"  />     // gate on arubaKeyConfigured
 *   <NoVendorKeysBanner vendor="either" />     // gate on BOTH missing
 *
 * The CTA primary button matches the gated vendor; the ghost button
 * points to the other vendor (since most pages still benefit from
 * either key being added).
 */
import { Link } from "react-router-dom";
import { KeyRound } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/stores/auth";

export interface NoVendorKeysBannerProps {
  vendor: "meraki" | "aruba" | "either";
  /** Override the default headline ("No vendor keys yet"). */
  title?: string;
  /** Override the default body copy. */
  description?: string;
}

export function NoVendorKeysBanner({
  vendor,
  title,
  description,
}: NoVendorKeysBannerProps) {
  const user = useAuth((s) => s.user);
  if (!user) return null;

  const merakiOk = user.merakiKeyConfigured === true;
  const arubaOk  = user.arubaKeyConfigured === true;

  // Decide whether to show. "either" only renders when BOTH are missing
  // (a single key satisfies an "either" page). Vendor-specific modes
  // render only when THAT vendor is missing.
  const show =
    vendor === "meraki" ? !merakiOk
    : vendor === "aruba"  ? !arubaOk
    : !merakiOk && !arubaOk;
  if (!show) return null;

  const defaultTitle =
    vendor === "meraki" ? "Add a Meraki key to use this page"
    : vendor === "aruba"  ? "Add an Aruba bundle to use this page"
    : "No vendor keys yet";

  const defaultDescription =
    vendor === "meraki"
      ? "This page calls Meraki Dashboard with your credentials. Without a key, every action will fail. Add one to continue."
      : vendor === "aruba"
        ? "This page calls Aruba Central with your credentials. Without a bundle, every action will fail. Upload one to continue."
        : "Opus calls each vendor with your credentials, not a shared one. Add a Meraki key or an Aruba bundle to populate this page.";

  // Primary CTA points at the gated vendor; ghost shows the alternative.
  const primaryTo   = vendor === "aruba" ? "/aruba/keys" : "/meraki/keys";
  const primaryLabel = vendor === "aruba" ? "Aruba bundle" : "Meraki key";
  const ghostTo     = vendor === "aruba" ? "/meraki/keys" : "/aruba/keys";
  const ghostLabel  = vendor === "aruba" ? "Meraki key" : "Aruba bundle";

  return (
    <Card>
      <CardContent className="flex items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-aurora-soft text-violet">
          <KeyRound size={20} />
        </div>
        <div className="flex-1">
          <div className="font-semibold text-fg">{title ?? defaultTitle}</div>
          <div className="text-sm text-fg-dim">{description ?? defaultDescription}</div>
        </div>
        <div className="flex gap-2">
          {vendor === "either" || vendor !== "meraki" ? (
            <Button asChild size="sm" variant="ghost">
              <Link to={ghostTo}>
                <KeyRound size={13} />
                {ghostLabel}
              </Link>
            </Button>
          ) : null}
          <Button asChild size="sm" variant="primary">
            <Link to={primaryTo}>
              <KeyRound size={13} />
              {primaryLabel}
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
